import { defineStore } from "pinia";
import { reactive, ref, set, del } from "vue";
import { useIQChatStore } from "./iqChatStore";
import configService from "@/services/config";
import {
  buildVideoToComponentsPayload,
  buildIqVideoSuggestEndpoint,
} from "@/utils/buildVideoToComponentsPayload";
import {
  buildVideoToComponentsCheckpointPatch,
  isInlineVideoToComponentsPassSuccess,
} from "@/utils/buildVideoToComponentsCheckpointPatch";
import { isContextValidationFail } from "@/utils/buildContextValidationSuggestedInstructions";
import {
  changeCheckpointV2,
  saveVideoInspectionInstructions,
  notifyCheckpointPresenceUpdated,
} from "@/api/responsibilityAPI";
import store from "@/store";

function extractVideoUrl(iframeHtml) {
  if (!iframeHtml) return "";
  if (!iframeHtml.includes("<iframe") && !iframeHtml.includes("src=")) {
    return iframeHtml;
  }
  const srcMatch = iframeHtml.match(/src=\\?"([^"\\]+)/);
  if (srcMatch && srcMatch[1]) return srcMatch[1];
  const fallbackMatch = iframeHtml.match(/src="([^"]+)"/);
  if (fallbackMatch && fallbackMatch[1]) return fallbackMatch[1];
  return "";
}

function findLastVideoInstructionSuggestions(messages) {
  const list = (messages || []).filter((m) => m.type === "ai");
  for (let i = list.length - 1; i >= 0; i--) {
    const c = list[i].content;
    if (c && typeof c === "object" && c.suggestions != null) {
      const s = c.suggestions;
      if (typeof s === "object" && s !== null) {
        return s;
      }
    }
  }
  return null;
}

function inferMismatchSuggestionsFromLastMessage(messages, videoUrl) {
  const list = (messages || []).filter((m) => m.type === "ai");
  const last = list[list.length - 1];
  if (!last) return null;

  const videoUrlResolved = extractVideoUrl(videoUrl) || videoUrl || null;
  const mismatchFallback = "This video does not match the context of the checkpoint!";

  if (last.content && typeof last.content === "object") {
    const explicitError =
      last.content.error ||
      last.content.answer ||
      (typeof last.content.validation?.reasoning === "string"
        ? last.content.validation.reasoning
        : null);
    if (explicitError) {
      return {
        steps: [],
        summary: null,
        error: explicitError || mismatchFallback,
        video_url: videoUrlResolved,
      };
    }
  }

  if (typeof last.content === "string" && last.content.trim()) {
    return {
      steps: [],
      summary: null,
      error: last.content.trim(),
      video_url: videoUrlResolved,
    };
  }

  return null;
}

function getAccountId() {
  try {
    return JSON.parse(localStorage.getItem("currentAccountID"));
  } catch {
    return null;
  }
}

/**
 * ER-12337 — persistent video-microlearning generation runs, keyed by checkpoint id.
 *
 * The runner lives here (not in CheckpointEditFormV2 / MiniIQOverlay) so an in-flight
 * generate-microlearning survives the editor unmounting when the operator switches
 * responsibility tabs. The stream, reasoning narrative, started-at timestamp and status
 * are all held per checkpoint and rehydrated on remount.
 */
export const useIqVideoMicrolearningStore = defineStore("iqVideoMicrolearning", () => {
  const iqChatStore = useIQChatStore();

  // checkpointId -> run
  const runs = reactive({});

  // Vue 2.7's reactive() does not track dynamic keys (set()/del() on a missing key is
  // invisible to a computed that first evaluated before that key existed). A version
  // counter gives computeds a stable dependency to re-evaluate on, then they re-read
  // runs[key] directly. Bump it after EVERY mutation of `runs`.
  const runVersion = ref(0);
  function bumpRunVersion() {
    runVersion.value += 1;
  }

  function getRun(checkpointId) {
    // Register a reactive dependency on every run mutation (see runVersion above).
    void runVersion.value;
    if (checkpointId == null) return null;
    return runs[String(checkpointId)] || null;
  }

  function hasActiveRun(checkpointId) {
    return getRun(checkpointId)?.status === "thinking";
  }

  /**
   * Abort a run's own request. Each run owns its AbortController (passed to
   * generateAIResponse as an external signal), so unrelated IQ surfaces calling
   * stopGenerating()/clearChat() can no longer cancel a background run.
   */
  function abortRunController(run) {
    if (!run || !run.controller) return;
    try {
      run.controller.abort();
    } catch {
      // AbortController may be unavailable in isolated unit tests.
    }
    run.controller = null;
  }

  /** Abort any OTHER checkpoint's active run (single shared iqChatStore UI state). */
  function abortOtherActiveRuns(exceptCheckpointId) {
    const key = String(exceptCheckpointId);
    let hadOtherActive = false;
    Object.keys(runs).forEach((id) => {
      if (id !== key && runs[id]?.status === "thinking") {
        abortRunController(runs[id]);
        runs[id].status = "failed";
        runs[id].outcome = "cancelled";
        runs[id].error = "Generation superseded by another checkpoint.";
        hadOtherActive = true;
      }
    });
    if (hadOtherActive) {
      bumpRunVersion();
      try {
        iqChatStore.stopGenerating();
        iqChatStore.clearChat();
      } catch {
        // Pinia may be unavailable in isolated unit tests.
      }
    }
  }

  function startRun(params) {
    const key = String(params.checkpointId);
    if (runs[key]?.status === "thinking") {
      return getRun(key);
    }
    abortOtherActiveRuns(key);
    // Vue 2.7: a dynamic key assigned to a reactive object is not itself reactive
    // unless added via `set` — without this the banner/disable state never re-renders.
    set(runs, key, {
      status: "thinking",
      outcome: null,
      error: null,
      startedAt: Date.now(),
      finishedAt: null,
      params: { ...params },
      // Owned by this run so navigation / other IQ surfaces cannot abort it.
      controller: null,
    });
    bumpRunVersion();
    void runGeneration(key);
    return runs[key];
  }

  function retryRun(checkpointId) {
    const run = getRun(checkpointId);
    if (!run || !run.params) return run;
    abortRunController(run);
    run.status = "thinking";
    run.outcome = null;
    run.error = null;
    run.startedAt = Date.now();
    run.finishedAt = null;
    bumpRunVersion();
    void runGeneration(String(checkpointId));
    return run;
  }

  /** Exit Edit / Cancel and Discard / lock loss — cancel and forget this checkpoint's run. */
  function abortRun(checkpointId) {
    const key = String(checkpointId);
    const run = runs[key];
    if (!run) return;
    abortRunController(run);
    if (run.status === "thinking") {
      try {
        // Clear the shared IQ UI state for this run; the run's network request is
        // cancelled by its own controller above.
        iqChatStore.stopGenerating();
      } catch {
        // Pinia may be unavailable in isolated unit tests.
      }
    }
    del(runs, key);
    bumpRunVersion();
  }

  /**
   * Session teardown (logout / account switch): cancel every in-flight request and
   * drop all per-checkpoint state so the next authenticated session starts clean.
   */
  function clearAllRuns() {
    Object.keys(runs).forEach((id) => {
      abortRunController(runs[id]);
      del(runs, id);
    });
    bumpRunVersion();
    try {
      iqChatStore.stopGenerating();
      iqChatStore.clearChat();
    } catch {
      // Pinia may be unavailable in isolated unit tests.
    }
  }

  async function runGeneration(checkpointId) {
    const key = String(checkpointId);
    const run = runs[key];
    if (!run || !run.params) return;

    const p = run.params;
    const iqBaseUrl = configService.get("iqBaseUrl");
    const apiEndpoint = buildIqVideoSuggestEndpoint(iqBaseUrl, true);
    const currentAccountDetails = JSON.parse(localStorage.getItem("currentAccountDetails") || "{}");
    const businessPurpose = currentAccountDetails.purpose || "";
    const extractedVideoUrl = extractVideoUrl(p.videoUrl);
    const checkpointCriteria = `${p.checkpointPrefix || ""} ${p.checkpointCriteria || ""}`.trim();
    const customPayload = buildVideoToComponentsPayload({
      videoUrl: extractedVideoUrl,
      businessPurpose,
      responsibilityName: p.responsibilityName || "",
      responsibilityPurpose: p.responsibilityPurpose || "",
      checkpointCriteria,
      language: "english",
      microlearningSelection: p.selection || null,
    });

    // Own the request lifecycle for this run. Passing this signal through makes the
    // stream independent of iqChatStore's shared stop/clear actions, so navigating
    // away from the editor cannot cancel the background generation.
    const controller = new AbortController();
    run.controller = controller;

    try {
      iqChatStore.clearChat();
      await iqChatStore.generateAIResponse(
        p.userPrompt,
        5,
        apiEndpoint,
        customPayload,
        null,
        null,
        { signal: controller.signal }
      );
    } catch (err) {
      // generateAIResponse swallows AbortError; any other throw means the stream died.
      if (runs[key]?.status === "thinking") {
        runs[key].status = "failed";
        runs[key].outcome = "error";
        runs[key].error = "Content generation failed.";
        bumpRunVersion();
      }
      return;
    } finally {
      if (runs[key]?.controller === controller) {
        runs[key].controller = null;
      }
    }

    // The run may have been aborted or superseded while the stream was in flight.
    if (!runs[key] || runs[key].status !== "thinking") return;

    const messages = iqChatStore.messages;
    let suggestedInstructions = findLastVideoInstructionSuggestions(messages);
    if (!suggestedInstructions) {
      suggestedInstructions = inferMismatchSuggestionsFromLastMessage(messages, p.videoUrl);
    }

    if (!suggestedInstructions) {
      runs[key].status = "failed";
      runs[key].outcome = "error";
      runs[key].error = "Content generation failed.";
      bumpRunVersion();
      return;
    }

    const isFail = isContextValidationFail(suggestedInstructions);

    if (isFail) {
      const withError = {
        ...suggestedInstructions,
        error:
          suggestedInstructions?.error ??
          suggestedInstructions?.validation?.reasoning ??
          suggestedInstructions?.inspection_instructions?.validation?.reasoning ??
          "Video validation failed.",
      };
      await persistResult(key, withError, "fail");
      // Even if persist fails, the mismatch verdict is what the editor must surface.
      // The run may have been aborted (logout / Exit Edit) while the PATCH was in flight.
      if (!runs[key]) return;
      runs[key].status = "failed";
      runs[key].outcome = "mismatch";
      runs[key].error = withError.error;
      bumpRunVersion();
      return;
    }

    if (isInlineVideoToComponentsPassSuccess(suggestedInstructions, p.selection)) {
      const saved = await persistResult(key, suggestedInstructions, "pass");
      if (!runs[key]) return;
      runs[key].status = saved ? "done" : "failed";
      runs[key].outcome = saved ? "pass" : "error";
      runs[key].error = saved ? null : "Could not save the generated microlearning.";
      if (saved) {
        runs[key].finishedAt = Date.now();
      }
      bumpRunVersion();
      return;
    }

    runs[key].status = "failed";
    runs[key].outcome = "error";
    runs[key].error = "Content generation failed.";
    bumpRunVersion();
  }

  async function persistResult(key, suggestedInstructions, mode) {
    const run = runs[key];
    if (!run || !run.params) return false;
    const accountId = getAccountId();
    if (!accountId || run.params.responsibilityId == null || run.params.checkpointId == null) {
      return false;
    }

    try {
      let patch = buildVideoToComponentsCheckpointPatch({
        suggestedInstructions,
        mode,
        microlearningSelection: run.params.selection,
      });

      if (mode === "fail") {
        const videoUrl =
          suggestedInstructions?.video_url ??
          (extractVideoUrl(String(run.params.videoUrl || "")) || null);
        if (videoUrl && patch.proposed_instructions) {
          const parsed = JSON.parse(patch.proposed_instructions);
          patch = {
            ...patch,
            proposed_instructions: JSON.stringify({ ...parsed, video_url: videoUrl }),
          };
        }
      }

      await changeCheckpointV2(
        accountId,
        run.params.responsibilityId,
        run.params.checkpointId,
        patch
      );

      if (mode === "pass" && store.getters["featureFlags/ideaMDP427"]) {
        await saveVideoInspectionInstructions(
          accountId,
          run.params.responsibilityId,
          run.params.checkpointId,
          { inspection_instructions: suggestedInstructions }
        );
      }

      try {
        const response = await notifyCheckpointPresenceUpdated(accountId, run.params.checkpointId);
        if (response?.data?.broadcast === false) {
          await notifyCheckpointPresenceUpdated(accountId, run.params.checkpointId);
        }
      } catch {
        // Optional presence notify — the save already succeeded.
      }

      return true;
    } catch (err) {
      console.error("[iqVideoMicrolearningStore] persist video_to_components result:", err);
      return false;
    }
  }

  return {
    runs,
    getRun,
    hasActiveRun,
    startRun,
    retryRun,
    abortRun,
    clearAllRuns,
  };
});
